import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { API_BASE_URL, TOKEN_KEY } from '@/utils/constants'
import type { ApiResponse, ApiError } from '@/types/api'

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Add auth token if exists
        const token = localStorage.getItem(TOKEN_KEY)
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`
        }

        // Add tenant ID if exists (for future multi-tenancy)
        const tenantId = localStorage.getItem('fuelflow_tenant_id')
        if (tenantId && config.headers) {
          config.headers['X-Tenant-ID'] = tenantId
        }

        // Remove Content-Type for FormData - let axios set it with boundary
        if (config.data instanceof FormData && config.headers) {
          delete config.headers['Content-Type']
        }

        return config
      },
      error => {
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response
      },
      async (error: AxiosError) => {
        if (error.response) {
          const status = error.response.status

          // Handle 401 Unauthorized - Token expired or invalid
          if (status === 401) {
            // Clear auth data
            localStorage.removeItem(TOKEN_KEY)
            localStorage.removeItem('fuelflow_user')

            // Only redirect if not already on login page and not during initial auth check
            // The auth check happens silently on app start
            const isAuthMeRequest = error.config?.url?.includes('/auth/me')
            if (window.location.pathname !== '/login' && !isAuthMeRequest) {
              window.location.href = '/login?session_expired=1'
            }
          }

          // Handle 403 Forbidden
          if (status === 403) {
            console.error('Access forbidden')
          }

          // Handle 422 Validation Error
          if (status === 422) {
            const responseData = error.response.data as any
            const apiError: ApiError = {
              message: responseData?.message || 'Validation error',
              status: status,
              errors: responseData?.errors,
            }
            return Promise.reject(apiError)
          }

          // Handle 500 Server Error
          if (status >= 500) {
            console.error('Server error:', error)
          }
        } else if (error.request) {
          // Network error
          console.error('Network error:', error.message)
        }

        return Promise.reject(error)
      }
    )
  }

  // GET request - accepts params object directly (not wrapped in {params: ...})
  public async get<T = any>(url: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    const response = await this.client.get<ApiResponse<T>>(url, { params })
    return response.data
  }

  // POST request
  public async post<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.client.post<ApiResponse<T>>(url, data)
    return response.data
  }

  // PUT request
  public async put<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.client.put<ApiResponse<T>>(url, data)
    return response.data
  }

  // PATCH request
  public async patch<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.client.patch<ApiResponse<T>>(url, data)
    return response.data
  }

  // DELETE request
  public async delete<T = any>(url: string): Promise<ApiResponse<T>> {
    const response = await this.client.delete<ApiResponse<T>>(url)
    return response.data
  }

  // Upload file
  // Note: Do NOT set Content-Type header manually - axios will set it automatically
  // with the correct boundary parameter when FormData is detected
  public async upload<T = any>(url: string, formData: FormData): Promise<ApiResponse<T>> {
    const response = await this.client.post<ApiResponse<T>>(url, formData)
    return response.data
  }

  // Download file
  public async download(url: string, filename: string): Promise<void> {
    const response = await this.client.get(url, {
      responseType: 'blob',
    })

    // Preserve the content type from the response
    const contentType = response.headers['content-type'] || 'application/octet-stream'
    const blob = new Blob([response.data], { type: contentType })
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }

  // Get raw axios instance for custom requests
  public getClient(): AxiosInstance {
    return this.client
  }
}

// Export singleton instance
export const apiClient = new ApiClient()
export default apiClient
